// ============================================
// Parser ChordPro nativo — regex
// ============================================

export interface ChordProLine {
  type: "lyric" | "directive" | "empty";
  segments: ChordProSegment[];
  raw: string;
}

export interface ChordProSegment {
  chord: string | null;
  text: string;
}

export interface ChordProSong {
  title: string;
  artist: string;
  capo: number;
  key: string;
  tempo: number;
  lines: ChordProLine[];
  chords: Set<string>;
}

const CHORD_PATTERN = /\[([^\]]+)\]/g;

function parseLine(raw: string): ChordProLine {
  const trimmed = raw.trimEnd();
  if (!trimmed.trim()) return { type: "empty", segments: [], raw };

  // Directiva: {key: value} o {command}
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    return {
      type: "directive",
      segments: [{ chord: null, text: trimmed }],
      raw,
    };
  }

  // Lírica con acordes
  const segments: ChordProSegment[] = [];
  const matches = Array.from(trimmed.matchAll(CHORD_PATTERN));

  if (matches.length === 0) {
    return {
      type: "lyric",
      segments: [{ chord: null, text: trimmed }],
      raw,
    };
  }

  let pos = 0;
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;
    const chord = match[1].trim();

    // Texto antes del acorde
    if (matchStart > pos) {
      segments.push({ chord: null, text: trimmed.slice(pos, matchStart) });
    }

    // Texto después del acorde hasta el próximo acorde
    const nextStart = i + 1 < matches.length ? matches[i + 1].index! : trimmed.length;
    const textAfter = trimmed.slice(matchEnd, nextStart);

    // Si el texto después empieza con otro acorde consecutivo, usar \u00A0
    if (textAfter.trimStart().startsWith("[")) {
      segments.push({ chord, text: "\u00A0" });
      pos = nextStart;
      continue;
    }

    // El acorde "flota" sobre la primera sílaba del texto que le sigue
    const syllableMatch = textAfter.match(/^(\s*)(\S?)(.*)$/);
    if (syllableMatch && syllableMatch[2]) {
      const [, space, firstChar, rest] = syllableMatch;
      segments.push({ chord, text: space + firstChar });
      if (rest) segments.push({ chord: null, text: rest });
    } else {
      // Si textAfter solo tiene espacios (acordes consecutivos), usar \u00A0
      // para asegurar ancho visual en el renderizado
      const normalized = textAfter.trim() === "" ? "\u00A0" : textAfter;
      segments.push({ chord, text: normalized || "\u00A0" });
    }

    pos = nextStart;
  }

  return { type: "lyric", segments, raw };
}

export function parseChordPro(raw: string): ChordProSong {
  const lines = raw.split("\n");
  const song: ChordProSong = {
    title: "",
    artist: "",
    capo: 0,
    key: "",
    tempo: 120,
    lines: [],
    chords: new Set(),
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();

    // Directivas metadata
    const metaMatch = trimmed.match(/^\{(\w+):\s*(.*)\}$/);
    if (metaMatch) {
      const [, key, value] = metaMatch;
      switch (key.toLowerCase()) {
        case "title":
        case "t":
          song.title = value.trim();
          continue;
        case "artist":
        case "a":
          song.artist = value.trim();
          continue;
        case "capo":
          song.capo = parseInt(value, 10) || 0;
          continue;
        case "key":
        case "k":
          song.key = value.trim();
          continue;
        case "tempo":
          song.tempo = parseInt(value, 10) || 120;
          continue;
      }
    }

    const parsedLine = parseLine(rawLine);
    song.lines.push(parsedLine);

    // Extraer acordes
    let chordMatch: RegExpExecArray | null;
    while ((chordMatch = CHORD_PATTERN.exec(rawLine)) !== null) {
      song.chords.add(chordMatch[1].trim());
    }
    CHORD_PATTERN.lastIndex = 0;
  }

  return song;
}

export function songToChordPro(song: ChordProSong): string {
  const lines: string[] = [];
  if (song.title) lines.push(`{title: ${song.title}}`);
  if (song.artist) lines.push(`{artist: ${song.artist}}`);
  if (song.key) lines.push(`{key: ${song.key}}`);
  if (song.capo) lines.push(`{capo: ${song.capo}}`);
  if (song.tempo !== 120) lines.push(`{tempo: ${song.tempo}}`);
  lines.push("");

  for (const line of song.lines) {
    if (line.type === "empty") {
      lines.push("");
    } else if (line.type === "directive") {
      lines.push(line.segments[0].text);
    } else {
      let out = "";
      for (const seg of line.segments) {
        if (seg.chord) {
          out += `[${seg.chord}]${seg.text}`;
        } else {
          out += seg.text;
        }
      }
      lines.push(out);
    }
  }

  return lines.join("\n");
}
